import { api } from './api';

// Investors added by hand travel only when there are some: a lender merge
// refuses the field outright.
const withInvestors = (body, investorIds) =>
    (investorIds && investorIds.length ? { ...body, investor_ids: investorIds } : body);

// A vendor or agent merge names its contacts and no property; the server
// refuses either field where it does not belong.
const withPeople = (body, propertyIds, contactIds) => (contactIds && contactIds.length
    ? { ...body, contact_ids: contactIds }
    : { ...body, property_ids: propertyIds });

function payload({ propertyIds, contactIds, subject, bodyHtml, mode, choices, overrides, excluded, attachments, audience, listingIds, listingOverrides, investorIds }) {
    return withInvestors(withPeople({
        subject,
        body_html: bodyHtml,
        property_mode: mode,
        property_choices: choices,
        overrides,
        excluded,
        audience: audience || undefined,
        // Only where each file sits travels; the server reads the bytes itself.
        attachments: (attachments || []).map(({ path, name, mime }) => ({ path, name, mime })),
        listing_ids: listingIds || [],
        listing_overrides: listingOverrides || {},
    }, propertyIds, contactIds), investorIds);
}

// Reaching the owners of a filtered property list.
//
// The property ids come from the list screen rather than the filters: those
// still live in the Arc queries the list runs, and they carry any manual
// Remove Selected / Remove Unselected narrowing the server never sees.
export const mailMergeApi = {
    // The whole envelope: meta carries the counts the page leads with and the
    // sender identity it has to show.
    recipients: (propertyIds, audience, investorIds, contactIds) => api.post(
        '/mail-merge/recipients',
        withInvestors(withPeople({ audience: audience || undefined }, propertyIds, contactIds), investorIds),
    ),

    // The investor search the parcel map already uses: name, email, company,
    // second contact or address, optionally within one state. The whole
    // envelope, for the total and the last page.
    searchInvestors: ({ search, state, page, perPage }) => api.get('/map/investors', {
        search: search || undefined,
        state: state || undefined,
        page,
        per_page: perPage,
    }),
    // The picker's "All states" list.
    investorStates: () => api.get('/map/investors/states').then((envelope) => envelope.data || []),

    // Every Summit listing a buyside letter can market, with the figures the
    // letter prints for each.
    listings: () => api.get('/mail-merge/listings').then((res) => res.data),

    // One file, uploaded as soon as it is picked. Resolves to { path, name,
    // size, mime }, which is what the draft keeps and the send names.
    uploadAttachment: (file) => {
        const form = new FormData();
        form.append('file', file);
        return api.postForm('/mail-merge/attachments', form).then((res) => res.data);
    },

    // One object rather than eight arguments, and the same one for both calls:
    // `mode` is how much of a portfolio the property tokens name, `choices`
    // the one property that speaks for an owner who has several, `overrides`
    // what the broker typed where a record was blank, and `excluded` the
    // owners they decided to leave out, and `attachments` the uploaded files
    // to attach. A buyside letter adds `listingIds`, the Summit listings it
    // markets, and `listingOverrides`, what was typed once where they are blank.
    send: (selection) => api.post('/mail-merge/send', payload(selection)).then((res) => res.data),

    sendTest: (selection) =>
        api.post('/mail-merge/send', { ...payload(selection), test_only: true }).then((res) => res.data),

    run: (runId) => api.get('/mail-merge/runs/' + runId).then((res) => res.data),
};

// The member's own Outlook mailbox, which is what a merge is sent through.
// Connecting is a full-page trip to Microsoft and back, so this only hands
// over the address to go to.
export const outlookApi = {
    authorizeUrl: () => api.post('/microsoft/connect').then((res) => res.data.authorize_url),
    disconnect: () => api.del('/microsoft/connect').then((res) => res.data),
};
